import React, { useState, useEffect } from "react";
import {
  Col,
  Nav,
  NavItem,
  NavLink,
  Row,
  TabContent,
  TabPane,
  Offcanvas,
  OffcanvasHeader,
  OffcanvasBody,
  CardHeader,
  CardBody,
  CardFooter,
  Card,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  FormFeedback,
  Alert,
} from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import Select from "react-select";
import classnames from "classnames";
import { sendGetRequestApi, sendPostRequestApi } from "../../services/service";
import { roles } from "../../config/roles";
import { cloud_tech } from "../../config/technologies/cloud_tech";
import { databases } from "../../config/technologies/databases";
import { machine_learning } from "../../config/technologies/machine_learning";
import { programming_languages } from "../../config/technologies/programming_languages";
import { java_technologies } from "../../config/technologies/java_technologies";
import { web_technologies } from "../../config/technologies/web_technologies";
import { mobile_tech } from "../../config/technologies/mobile_tech";
import { analytics } from "../../config/technologies/analytics";
import { devops } from "../../config/technologies/devops";
import { ecommerce } from "../../config/technologies/ecommerce";
import { salesforce } from "../../config/technologies/salesforce";
import { sap } from "../../config/technologies/sap";
import { toast } from "react-toastify";

const CandidateInviteCanvas = ({ isOpen, toggleRightCanvas }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [enumJobRole, setEnumJobRole] = useState([]);
  const [skills, setskills] = useState([]);
  const [testUrl, setTestUrl] = useState("");

  // Helper function to find skill in all skill arrays
  const findSkillByTitle = (skillTitle) => {
    if (!skillTitle) return null;

    const allSkills = [
      ...cloud_tech,
      ...databases,
      ...machine_learning,
      ...programming_languages,
      ...java_technologies,
      ...web_technologies,
      ...mobile_tech,
      ...analytics,
      ...devops,
      ...ecommerce,
      ...salesforce,
      ...sap,
    ];

    return allSkills.find(
      (skill) => skill.title?.toLowerCase() === skillTitle.toLowerCase()
    );
  };

  // Helper function to get primary skills for a role
  const getPrimarySkillsForRole = (roleTitle) => {
    const role = roles.find((r) => r.title === roleTitle);
    if (!role) return [];

    const primarySkills = [];
    const skillFields = [
      "primary_1",
      "primary_2",
      "primary_3",
      "primary_4",
      "primary_5",
    ];

    skillFields.forEach((field) => {
      const skillTitle = role[field];
      if (skillTitle && skillTitle.trim() !== "") {
        const foundSkill = findSkillByTitle(skillTitle);
        if (foundSkill) {
          primarySkills.push({
            value: foundSkill.title,
            label: foundSkill.title,
          });
        }
      }
    });

    return primarySkills;
  };

  useEffect(() => {
    sendGetRequestApi("v1/enumjobrole?limit=2000").then((response) => {
      console.log("enum roles", response);
      if (response?.success) {
        const roleOptions = roles.map((role) => ({
          value: role?.title,
          label: role?.title,
        }));
        setEnumJobRole(roleOptions);
      }
    });

    const allSkills = cloud_tech
      .concat(...databases)
      .concat(...machine_learning)
      .concat(...programming_languages)
      .concat(...java_technologies)
      .concat(...web_technologies)
      .concat(...mobile_tech)
      .concat(...analytics)
      .concat(...devops)
      .concat(...ecommerce)
      .concat(...salesforce)
      .concat(...sap);

    const skillOptions = allSkills.map((skill) => ({
      value: skill?.title,
      label: skill?.title,
    }));

    setskills(skillOptions);
  }, []);

  // Base validation schema
  const validationSchema = Yup.object({
    firstName: Yup.string()
      .required("First name is required")
      .min(2, "First name must be at least 2 characters")
      .max(30, "First name must be less than 30 characters")
      .matches(/^[a-zA-Z\s]+$/, "First name can only contain letters"),

    lastName: Yup.string()
      .required("Last name is required")
      .min(2, "Last name must be at least 2 characters")
      .max(30, "Last name must be less than 30 characters")
      .matches(/^[a-zA-Z\s]+$/, "Last name can only contain letters"),

    email: Yup.string()
      .required("Email is required")
      .email("Please enter a valid email address")
      .max(100, "Email must be less than 100 characters"),

    role: Yup.object().required("Role is required").nullable(),

    skills: Yup.array()
      .required("At least one skill is required")
      .min(1, "Please select at least one skill")
      .max(10, "You can select maximum 10 skills"),
  });

  // Custom validation function for experience fields
  const validateExperienceFields = (values) => {
    const errors = {};

    if (values.skills && values.skills.length > 0) {
      values.skills.forEach((skill) => {
        const experienceField = `${skill.value}_experience`;
        const experienceValue = values[experienceField];

        if (!experienceValue && experienceValue !== 0) {
          errors[experienceField] = `Experience for ${skill.label} is required`;
        } else if (experienceValue < 0) {
          errors[experienceField] = "Experience cannot be negative";
        } else if (experienceValue > 50) {
          errors[experienceField] = "Experience cannot exceed 50 years";
        }
      });
    }

    return errors;
  };

  // Formik setup
  const formik = useFormik({
    initialValues: {
      firstName: "",
      lastName: "",
      email: "",
      role: null,
      skills: [],
    },
    validationSchema: validationSchema,
    validate: (values) => {
      // Combine Yup validation errors with custom experience validation
      const experienceErrors = validateExperienceFields(values);
      return experienceErrors;
    },
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: async (values, { resetForm }) => {
      setIsSubmitting(true);
      try {
        // Helper function to get skill category
        const getSkillCategory = (skillTitle) => {
          const allSkillsWithCategory = [
            ...cloud_tech,
            ...databases,
            ...machine_learning,
            ...programming_languages,
            ...java_technologies,
            ...web_technologies,
            ...mobile_tech,
            ...analytics,
            ...devops,
            ...ecommerce,
            ...salesforce,
            ...sap,
          ];

          const foundSkill = allSkillsWithCategory.find(
            (skill) => skill.title?.toLowerCase() === skillTitle.toLowerCase()
          );

          return foundSkill?.category || "Other";
        };

        // Create first object - Basic candidate info
        const candidateInfo = {
          firstName: values.firstName,
          lastName: values.lastName,
          email: values.email,
          skills: values.skills.map((skill) => skill.value),
        };

        // Create second object - Detailed assessment data
        const primarySkillsData = values.skills.map((skill) => ({
          title: skill.value,
          category: getSkillCategory(skill.value),
          experienceInYear: parseInt(values[`${skill.value}_experience`]) || 0,
        }));

        sendPostRequestApi("/resume/manual-create", candidateInfo).then(
          (res) => {
            console.log("createcandidateresponse", res);

            if (res?.success) {
              const assessmentData = {
                candidateId: res && res?.data?.candidateId, // This should be generated or received from the first API call
                role: values.role?.value || "",
                primary_skills: primarySkillsData,
                secondary_skills: [], // Empty array as per your requirement
              };

              sendPostRequestApi("/assessment",assessmentData).then((response) => {
                console.log("created assessment",response)
                if(response?.success){
                  setTestUrl(`https://me.talentiq.app/assessment?candidateID=${res?.data?.candidateId}&assessmentId=${response && response?.data?.assessmentId}&status=pending`);
                  toast.success("Assessment Created Successfully. Please share the below link.")
                }
              })
            }
          }
        );

        console.log("Candidate Info Object:", candidateInfo);

        // Here you would typically make two separate API calls:
        // 1. First call to create candidate and get candidateId
        // 2. Second call to create assessment with the received candidateId

        // Example API calls (uncomment and modify as needed):
        /*
        // First API call - Create candidate
        const candidateResponse = await sendPostRequestApi("v1/candidates", candidateInfo);
        
        if (candidateResponse?.success && candidateResponse?.data?.id) {
          // Update candidateId in assessment data
          assessmentData.candidateId = candidateResponse.data.id;
          
          // Second API call - Create assessment
          const assessmentResponse = await sendPostRequestApi("v1/assessments", assessmentData);
          
          if (assessmentResponse?.success) {
            console.log("Both candidate and assessment created successfully");
          }
        }
        */

        // Simulate API call
        await new Promise((resolve) => setTimeout(resolve, 2000));

        setSubmitSuccess(true);
        // resetForm();

        // Auto-hide success message after 3 seconds
        // setTimeout(() => {
        //   setSubmitSuccess(false);
        // }, 3000);
      } catch (error) {
        console.error("Submission error:", error);
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  // Handle role selection and auto-populate skills
  const handleRoleChange = (selectedRole) => {
    formik.setFieldValue("role", selectedRole);

    if (selectedRole) {
      const primarySkills = getPrimarySkillsForRole(selectedRole.value);

      // Remove duplicates and limit to available skills
      const availableSkills = primarySkills.filter((primarySkill) =>
        skills.some((skill) => skill.value === primarySkill.value)
      );

      // Auto-populate skills with primary skills for the role
      formik.setFieldValue("skills", availableSkills);

      // Clear any existing experience values
      Object.keys(formik.values).forEach((key) => {
        if (key.endsWith("_experience")) {
          formik.setFieldValue(key, "");
        }
      });

      console.log(
        `Auto-populated ${availableSkills.length} primary skills for role: ${selectedRole.label}`
      );
    } else {
      // Clear skills if no role is selected
      formik.setFieldValue("skills", []);
    }
  };

  // Update experience fields when skills change
  useEffect(() => {
    if (formik.values.skills) {
      // Add experience fields for selected skills
      formik.values.skills.forEach((skill) => {
        const experienceField = `${skill.value}_experience`;
        if (formik.values[experienceField] === undefined) {
          formik.setFieldValue(experienceField, "");
        }
      });
    }
  }, [formik.values.skills]);

  // Reset form when canvas is closed
  useEffect(() => {
    if (!isOpen) {
      formik.resetForm();
      setSubmitSuccess(false);
    }
  }, [isOpen]);

  // Custom styles for React Select
  const customSelectStyles = {
    control: (provided, state) => ({
      ...provided,
      borderRadius: "8px",
      border: "1px solid #ced4da",
      padding: "4px 8px",
      fontSize: "14px",
      minHeight: "45px",
      borderColor: state.isFocused
        ? "#0d6efd"
        : formik.touched.role && formik.errors.role
          ? "#dc3545"
          : "#ced4da",
      boxShadow: state.isFocused
        ? "0 0 0 0.2rem rgba(13, 110, 253, 0.25)"
        : null,
      "&:hover": {
        borderColor: state.isFocused ? "#0d6efd" : "#adb5bd",
      },
    }),
    placeholder: (provided) => ({
      ...provided,
      color: "#6c757d",
      fontSize: "14px",
    }),
    singleValue: (provided) => ({
      ...provided,
      color: "#495057",
      fontSize: "14px",
    }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: "#e7f1ff",
      borderRadius: "4px",
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      color: "#0d6efd",
      fontWeight: "500",
    }),
    multiValueRemove: (provided) => ({
      ...provided,
      color: "#0d6efd",
      "&:hover": {
        backgroundColor: "#dc3545",
        color: "white",
      },
    }),
  };

  const skillsSelectStyles = {
    ...customSelectStyles,
    control: (provided, state) => ({
      ...provided,
      borderRadius: "8px",
      border: "1px solid #ced4da",
      padding: "4px 8px",
      fontSize: "14px",
      minHeight: "45px",
      borderColor: state.isFocused
        ? "#0d6efd"
        : formik.touched.skills && formik.errors.skills
          ? "#dc3545"
          : "#ced4da",
      boxShadow: state.isFocused
        ? "0 0 0 0.2rem rgba(13, 110, 253, 0.25)"
        : null,
      "&:hover": {
        borderColor: state.isFocused ? "#0d6efd" : "#adb5bd",
      },
    }),
  };

  return (
    <React.Fragment>
      <div>
        <Offcanvas
          style={{
            width: "750px",
            zoom: "80%",
          }}
          isOpen={isOpen}
          direction="end"
          toggle={toggleRightCanvas}
        >
          <OffcanvasHeader
            className="bg-primary text-white border-0"
            toggle={toggleRightCanvas}
            style={{
              padding: "1.5rem",
            }}
          >
            <div>
              <h5 className="text-white mb-2 d-flex align-items-center">
                <i className="fas fa-user-plus me-3"></i>
                Invite Candidate
              </h5>
              <p className="text-white-50 mb-0 font-size-14">
                <i className="fas fa-info-circle me-2" />
                Send assessment invitation to potential candidates
              </p>
            </div>
          </OffcanvasHeader>

          <OffcanvasBody className="p-4" style={{ background: "#f8f9fa" }}>
            <Card className="border-0 shadow">
              <CardHeader className="bg-primary text-center border-0">
                <div className="py-3">
                  <i
                    className="fas fa-envelope-open-text mb-3"
                    style={{ fontSize: "2.5rem", color: "white" }}
                  ></i>
                  <h4 className="text-white mb-2">Candidate Invite Form</h4>
                  <p className="text-white-50 mb-0">
                    Fill in the details to send an assessment invitation
                  </p>
                </div>
              </CardHeader>

              <CardBody className="p-4">
                {submitSuccess && (
                  <Alert color="success" className="mb-4">
                    <div className="d-flex align-items-center">
                      <i className="fas fa-check-circle me-2"></i>
                      <strong>Success!</strong> Candidate invite sent
                      successfully!
                    </div>
                  </Alert>
                )}

                <Form onSubmit={formik.handleSubmit}>
                  <Row>
                    <Col lg={6}>
                      <FormGroup className="mb-3">
                        <Label
                          for="firstName"
                          className="form-label fw-semibold mb-2"
                        >
                          <i className="fas fa-user me-2 text-primary"></i>
                          First Name <span className="text-danger">*</span>
                        </Label>
                        <Input
                          type="text"
                          id="firstName"
                          name="firstName"
                          placeholder="Enter first name"
                          value={formik.values.firstName}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          invalid={
                            formik.touched.firstName &&
                            !!formik.errors.firstName
                          }
                          className="form-control"
                        />
                        {formik.touched.firstName &&
                          formik.errors.firstName && (
                            <FormFeedback>
                              {formik.errors.firstName}
                            </FormFeedback>
                          )}
                      </FormGroup>
                    </Col>

                    <Col lg={6}>
                      <FormGroup className="mb-3">
                        <Label
                          for="lastName"
                          className="form-label fw-semibold mb-2"
                        >
                          <i className="fas fa-user me-2 text-primary"></i>
                          Last Name <span className="text-danger">*</span>
                        </Label>
                        <Input
                          type="text"
                          id="lastName"
                          name="lastName"
                          placeholder="Enter last name"
                          value={formik.values.lastName}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          invalid={
                            formik.touched.lastName && !!formik.errors.lastName
                          }
                          className="form-control"
                        />
                        {formik.touched.lastName && formik.errors.lastName && (
                          <FormFeedback>{formik.errors.lastName}</FormFeedback>
                        )}
                      </FormGroup>
                    </Col>

                    <Col lg={12}>
                      <FormGroup className="mb-3">
                        <Label
                          for="email"
                          className="form-label fw-semibold mb-2"
                        >
                          <i className="fas fa-envelope me-2 text-primary"></i>
                          Email Address <span className="text-danger">*</span>
                        </Label>
                        <Input
                          type="email"
                          id="email"
                          name="email"
                          placeholder="Enter email address"
                          value={formik.values.email}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          invalid={
                            formik.touched.email && !!formik.errors.email
                          }
                          className="form-control"
                        />
                        {formik.touched.email && formik.errors.email && (
                          <FormFeedback>{formik.errors.email}</FormFeedback>
                        )}
                      </FormGroup>
                    </Col>

                    <Col lg={12}>
                      <FormGroup className="mb-3">
                        <Label
                          for="role"
                          className="form-label fw-semibold mb-2"
                        >
                          <i className="fas fa-briefcase me-2 text-primary"></i>
                          Role <span className="text-danger">*</span>
                        </Label>
                        <Select
                          id="role"
                          name="role"
                          options={enumJobRole}
                          value={formik.values.role}
                          onChange={handleRoleChange}
                          onBlur={() => formik.setFieldTouched("role", true)}
                          placeholder="Select a role"
                          isClearable
                          styles={customSelectStyles}
                        />
                        {formik.touched.role && formik.errors.role && (
                          <div className="text-danger small mt-1">
                            {formik.errors.role}
                          </div>
                        )}
                        {formik.values.role && (
                          <small className="text-info mt-1 d-block">
                            <i className="fas fa-magic me-1"></i>
                            Primary skills for this role have been
                            auto-populated below. You can modify them as needed.
                          </small>
                        )}
                      </FormGroup>
                    </Col>

                    <Col lg={12}>
                      <FormGroup className="mb-3">
                        <Label
                          for="skills"
                          className="form-label fw-semibold mb-2"
                        >
                          <i className="fas fa-code me-2 text-primary"></i>
                          Skills <span className="text-danger">*</span>
                        </Label>
                        <Select
                          id="skills"
                          name="skills"
                          options={skills}
                          value={formik.values.skills}
                          onChange={(selectedOptions) => {
                            formik.setFieldValue(
                              "skills",
                              selectedOptions || []
                            );
                          }}
                          onBlur={() => formik.setFieldTouched("skills", true)}
                          placeholder="Select skills (you can select multiple)"
                          isMulti
                          isClearable
                          styles={skillsSelectStyles}
                        />
                        {formik.touched.skills && formik.errors.skills && (
                          <div className="text-danger small mt-1">
                            {formik.errors.skills}
                          </div>
                        )}
                        <small className="text-muted mt-1 d-block">
                          <i className="fas fa-info-circle me-1"></i>
                          Selected:{" "}
                          <span className="fw-bold">
                            {formik.values.skills.length}
                          </span>
                          /10 skills
                        </small>
                      </FormGroup>
                    </Col>

                    {/* Dynamic Experience Fields for Selected Skills */}
                    {formik.values.skills.length > 0 && (
                      <Col lg={12}>
                        <div className="mb-3">
                          <Label className="form-label fw-semibold mb-3">
                            <i className="fas fa-calendar-alt me-2 text-primary"></i>
                            Experience for Selected Skills{" "}
                            <span className="text-danger">*</span>
                          </Label>
                          <div
                            className="border rounded p-3"
                            style={{ backgroundColor: "#f8f9fa" }}
                          >
                            <Row>
                              {formik.values.skills.map((skill, index) => (
                                <Col lg={6} key={skill.value} className="mb-3">
                                  <FormGroup>
                                    <Label
                                      for={`${skill.value}_experience`}
                                      className="form-label fw-medium mb-2"
                                    >
                                      <span className="badge bg-primary me-2">
                                        {skill.label}
                                      </span>
                                      Years of Experience
                                    </Label>
                                    <Input
                                      type="number"
                                      id={`${skill.value}_experience`}
                                      name={`${skill.value}_experience`}
                                      placeholder="Enter years"
                                      min="0"
                                      max="50"
                                      step="0.5"
                                      value={
                                        formik.values[
                                          `${skill.value}_experience`
                                        ] || ""
                                      }
                                      onChange={formik.handleChange}
                                      onBlur={formik.handleBlur}
                                      invalid={
                                        formik.touched[
                                          `${skill.value}_experience`
                                        ] &&
                                        !!formik.errors[
                                          `${skill.value}_experience`
                                        ]
                                      }
                                      className="form-control"
                                    />
                                    {formik.touched[
                                      `${skill.value}_experience`
                                    ] &&
                                      formik.errors[
                                        `${skill.value}_experience`
                                      ] && (
                                        <FormFeedback>
                                          {
                                            formik.errors[
                                              `${skill.value}_experience`
                                            ]
                                          }
                                        </FormFeedback>
                                      )}
                                  </FormGroup>
                                </Col>
                              ))}
                            </Row>
                          </div>
                        </div>
                      </Col>
                    )}
                  </Row>
                </Form>
              </CardBody>

              <CardFooter className="bg-light border-0 d-flex justify-content-between align-items-center p-3">
                <Button
                  color="secondary"
                  onClick={toggleRightCanvas}
                  disabled={isSubmitting}
                >
                  <i className="fas fa-times me-2"></i>
                  Cancel
                </Button>
                <Button
                  color="primary"
                  onClick={formik.handleSubmit}
                  disabled={isSubmitting || !formik.isValid}
                >
                  {isSubmitting ? (
                    <>
                      <i className="fas fa-spinner fa-spin me-2"></i>
                      Sending Invite...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-paper-plane me-2"></i>
                      Send Invite
                    </>
                  )}
                </Button>
              </CardFooter>

              {testUrl && <div className="d-flex  my-4 mx-2">
                <div className="w-25"><h3 className="fs-5">Assessment url:</h3></div>
                <div className="w-75"><p>{testUrl}</p></div>
              </div>}
            </Card>
          </OffcanvasBody>
        </Offcanvas>
      </div>
    </React.Fragment>
  );
};

export default CandidateInviteCanvas;
